import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { truncateParty, formatNumber, formatTime } from '../../utils/formatters';
import { API_BASE } from '../../utils/constants';
import { LoadingLogo } from '../LoadingLogo';

// Pagination settings
const PAGE_SIZE = 50;
const FETCH_SIZE = 1000; // API max per request
const MAX_PAGES_TO_FETCH = 10; // Fetch up to 10,000 transactions

// Transfer kind display names
const TRANSFER_KIND_LABELS = {
  'preapproval_send': 'Send',
  'transfer_instruction_accept': 'Accept',
  null: 'Claim',
};

export function TransactionsTab({ data, loading: globalLoading }) {
  const [allTransactions, setAllTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  // Filters
  const [filterKind, setFilterKind] = useState('all');
  const [filterMinAmount, setFilterMinAmount] = useState('');
  const [filterMaxAmount, setFilterMaxAmount] = useState('');

  // Build validator name lookup from data
  const partyNameLookup = useMemo(() => {
    const lookup = {};
    if (data?.validators) {
      for (const v of data.validators) {
        if (v.party && v.name) {
          lookup[v.party] = v.name;
        }
      }
    }
    return lookup;
  }, [data?.validators]);

  // Get display name for a party
  const getPartyName = useCallback((party) => {
    if (!party) return null;
    // Check validator lookup first
    if (partyNameLookup[party]) return partyNameLookup[party];
    // Extract name from party ID (before ::)
    const parts = party.split('::');
    if (parts[0] && parts[0].length < 50) {
      // Clean up auth0 prefixes
      let name = parts[0];
      if (name.startsWith('auth0_')) {
        return 'User ' + name.slice(6, 14) + '...';
      }
      return name;
    }
    return null;
  }, [partyNameLookup]);

  // Fetch transactions with pagination
  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    try {
      let allTxs = [];
      let afterOffset = null;
      let pagesFetched = 0;

      while (pagesFetched < MAX_PAGES_TO_FETCH) {
        const body = { page_size: FETCH_SIZE, sort_order: 'desc' };
        if (afterOffset) {
          body.after_offset = afterOffset;
        }

        const res = await fetch(`${API_BASE}/transactions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });

        if (!res.ok) break;

        const data = await res.json();
        const txs = data.transactions || [];
        allTxs = [...allTxs, ...txs];
        pagesFetched++;

        // Check if there are more pages
        if (txs.length < FETCH_SIZE) {
          break; // No more data
        }

        // Get the offset of the last transaction for next page
        const lastTx = txs[txs.length - 1];
        if (lastTx?.offset) {
          afterOffset = lastTx.offset;
        } else {
          break;
        }
      }

      setAllTransactions(allTxs);
    } catch (e) {
      console.warn('Failed to fetch transactions:', e);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  // Extract amount from transaction
  const getTransactionAmount = (tx) => {
    if (tx.transfer?.receivers?.[0]?.amount) {
      return parseFloat(tx.transfer.receivers[0].amount);
    }
    if (tx.tap?.amuletAmount) {
      return parseFloat(tx.tap.amuletAmount);
    }
    if (tx.mint?.amuletAmount) {
      return parseFloat(tx.mint.amuletAmount);
    }
    // For consolidations, use balance change
    if (tx.transfer?.balance_changes?.[0]?.change_to_initial_amount_as_of_round_zero) {
      const change = parseFloat(tx.transfer.balance_changes[0].change_to_initial_amount_as_of_round_zero);
      return Math.abs(change);
    }
    return 0;
  };

  // Filter transactions
  const filteredTransactions = useMemo(() => {
    return allTransactions.filter(tx => {
      // Kind filter
      if (filterKind !== 'all') {
        const kind = tx.transfer?.transfer_kind || null;
        if (filterKind === 'send' && kind !== 'preapproval_send') return false;
        if (filterKind === 'claim' && kind !== null) return false;
        if (filterKind === 'accept' && kind !== 'transfer_instruction_accept') return false;
      }

      // Amount filter
      const amount = getTransactionAmount(tx);
      if (filterMinAmount && amount < parseFloat(filterMinAmount)) return false;
      if (filterMaxAmount && amount > parseFloat(filterMaxAmount)) return false;

      return true;
    });
  }, [allTransactions, filterKind, filterMinAmount, filterMaxAmount]);

  // Calculate stats from filtered transactions
  const stats = useMemo(() => {
    let totalVolume = 0;
    let sendCount = 0;
    let claimCount = 0;
    let maxAmount = 0;
    let latestPrice = 0;

    for (const tx of filteredTransactions) {
      const amount = getTransactionAmount(tx);
      totalVolume += amount;
      if (amount > maxAmount) maxAmount = amount;

      const kind = tx.transfer?.transfer_kind;
      if (kind === 'preapproval_send') sendCount++;
      else claimCount++;

      if (tx.amulet_price && !latestPrice) {
        latestPrice = parseFloat(tx.amulet_price);
      }
    }

    return {
      totalVolume,
      count: filteredTransactions.length,
      avgAmount: filteredTransactions.length > 0 ? totalVolume / filteredTransactions.length : 0,
      maxAmount,
      sendCount,
      claimCount,
      latestPrice: latestPrice || data?.networkStats?.ccPrice || 0.10,
    };
  }, [filteredTransactions, data?.networkStats?.ccPrice]);

  // Pagination
  const totalPages = Math.ceil(filteredTransactions.length / PAGE_SIZE);
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const transactions = filteredTransactions.slice(startIndex, startIndex + PAGE_SIZE);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterKind, filterMinAmount, filterMaxAmount]);

  const isLoading = loading || globalLoading;

  return (
    <section style={{
      background: 'var(--bg-elevated)',
      border: '1px solid var(--border-default)',
      borderRadius: '8px',
      overflow: 'hidden',
    }}>
      {/* Stats Header */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        borderBottom: '1px solid var(--border-default)',
      }}>
        <div style={{
          padding: '16px',
          borderRight: '1px solid var(--border-subtle)',
          borderBottom: '1px solid var(--border-subtle)',
        }}>
          <div style={{ fontSize: '9px', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>
            Volume
          </div>
          <div style={{ fontSize: '18px', fontWeight: '600', color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>
            {formatNumber(stats.totalVolume)} CC
          </div>
          <div style={{ fontSize: '10px', color: 'var(--text-tertiary)', fontFamily: 'var(--font-mono)' }}>
            ${formatNumber(stats.totalVolume * stats.latestPrice)}
          </div>
        </div>

        <div style={{
          padding: '16px',
          borderBottom: '1px solid var(--border-subtle)',
        }}>
          <div style={{ fontSize: '9px', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>
            {allTransactions.length.toLocaleString()} Transactions
          </div>
          <div style={{ fontSize: '18px', fontWeight: '600', color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>
            {stats.count.toLocaleString()}
          </div>
          <div style={{ fontSize: '10px', color: 'var(--text-tertiary)' }}>
            {stats.sendCount} sends, {stats.claimCount} claims
          </div>
        </div>

        <div style={{
          padding: '16px',
          borderRight: '1px solid var(--border-subtle)',
        }}>
          <div style={{ fontSize: '9px', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>
            Avg TX
          </div>
          <div style={{ fontSize: '18px', fontWeight: '600', color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>
            {formatNumber(stats.avgAmount)} CC
          </div>
          <div style={{ fontSize: '10px', color: 'var(--text-tertiary)', fontFamily: 'var(--font-mono)' }}>
            ${formatNumber(stats.avgAmount * stats.latestPrice)}
          </div>
        </div>

        <div style={{
          padding: '16px',
        }}>
          <div style={{ fontSize: '9px', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>
            Largest TX
          </div>
          <div style={{ fontSize: '18px', fontWeight: '600', color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>
            {formatNumber(stats.maxAmount)} CC
          </div>
          <div style={{ fontSize: '10px', color: 'var(--text-tertiary)', fontFamily: 'var(--font-mono)' }}>
            ${formatNumber(stats.maxAmount * stats.latestPrice)}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div style={{
        padding: '10px 12px',
        borderBottom: '1px solid var(--border-default)',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '10px',
        alignItems: 'center',
        background: 'var(--bg-subtle)',
      }}>
        {/* Type filter */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ fontSize: '10px', color: 'var(--text-tertiary)', textTransform: 'uppercase' }}>Type:</span>
          <div style={{ display: 'flex', gap: '3px' }}>
            {[
              { value: 'all', label: 'All' },
              { value: 'send', label: 'Send' },
              { value: 'claim', label: 'Claim' },
            ].map(opt => (
              <button
                key={opt.value}
                onClick={() => setFilterKind(opt.value)}
                style={{
                  background: filterKind === opt.value ? 'var(--bg-elevated)' : 'transparent',
                  border: `1px solid ${filterKind === opt.value ? 'var(--border-strong)' : 'var(--border-subtle)'}`,
                  color: filterKind === opt.value ? 'var(--text-primary)' : 'var(--text-tertiary)',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  fontSize: '10px',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Amount range */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <span style={{ fontSize: '10px', color: 'var(--text-tertiary)', textTransform: 'uppercase' }}>Amt:</span>
          <input
            type="number"
            placeholder="Min"
            value={filterMinAmount}
            onChange={(e) => setFilterMinAmount(e.target.value)}
            style={{
              background: 'var(--bg-elevated)',
              border: '1px solid var(--border-subtle)',
              color: 'var(--text-primary)',
              padding: '4px 6px',
              borderRadius: '4px',
              fontSize: '10px',
              width: '55px',
              fontFamily: 'var(--font-mono)',
            }}
          />
          <span style={{ color: 'var(--text-tertiary)', fontSize: '10px' }}>-</span>
          <input
            type="number"
            placeholder="Max"
            value={filterMaxAmount}
            onChange={(e) => setFilterMaxAmount(e.target.value)}
            style={{
              background: 'var(--bg-elevated)',
              border: '1px solid var(--border-subtle)',
              color: 'var(--text-primary)',
              padding: '4px 6px',
              borderRadius: '4px',
              fontSize: '10px',
              width: '55px',
              fontFamily: 'var(--font-mono)',
            }}
          />
        </div>

        {/* Large TX filter shortcut */}
        <button
          onClick={() => {
            setFilterMinAmount('1000');
            setFilterMaxAmount('');
            setFilterKind('send');
          }}
          style={{
            background: 'transparent',
            border: '1px solid var(--border-subtle)',
            color: 'var(--text-secondary)',
            padding: '4px 10px',
            borderRadius: '4px',
            fontSize: '11px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          <span>Large TX</span>
        </button>

        {/* Clear filters */}
        {(filterKind !== 'all' || filterMinAmount || filterMaxAmount) && (
          <button
            onClick={() => {
              setFilterKind('all');
              setFilterMinAmount('');
              setFilterMaxAmount('');
            }}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--text-tertiary)',
              padding: '4px 8px',
              fontSize: '11px',
              cursor: 'pointer',
              textDecoration: 'underline',
            }}
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Desktop header */}
      <div className="hide-mobile" style={{
        display: 'grid',
        gridTemplateColumns: '80px minmax(0, 1.2fr) minmax(0, 1.2fr) 130px 90px 70px',
        gap: '16px',
        padding: '12px 24px',
        background: 'var(--bg-subtle)',
        fontSize: '11px',
        color: 'var(--text-tertiary)',
        textTransform: 'uppercase',
        letterSpacing: '1px',
      }}>
        <div>Type</div>
        <div>From</div>
        <div>To</div>
        <div style={{ textAlign: 'right' }}>Amount</div>
        <div style={{ textAlign: 'right' }}>Round</div>
        <div style={{ textAlign: 'right' }}>Time</div>
      </div>

      {/* Mobile header */}
      <div className="show-mobile-only" style={{
        display: 'flex',
        padding: '10px 12px',
        background: 'var(--bg-subtle)',
        fontSize: '9px',
        color: 'var(--text-tertiary)',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        alignItems: 'center',
      }}>
        <div style={{ width: '45px', flexShrink: 0 }}>Type</div>
        <div style={{ flex: 1 }}>Parties</div>
        <div style={{ width: '70px', textAlign: 'right', flexShrink: 0 }}>Amount</div>
        <div style={{ width: '35px', textAlign: 'right', flexShrink: 0 }}>Time</div>
      </div>

      {/* Table Body */}
      <div style={{ maxHeight: 'calc(100vh - 420px)', overflow: 'auto' }}>
        {isLoading ? (
          <LoadingLogo size={40} />
        ) : transactions.length > 0 ? (
          transactions.map((tx, i) => {
            const kind = tx.transfer?.transfer_kind || null;
            const kindLabel = TRANSFER_KIND_LABELS[kind] || 'Transfer';

            const sender = tx.transfer?.sender?.party;
            const receiver = tx.transfer?.receivers?.[0]?.party;
            const amount = getTransactionAmount(tx);
            const usdAmount = amount * (parseFloat(tx.amulet_price) || stats.latestPrice);

            const senderName = getPartyName(sender);
            const receiverName = getPartyName(receiver);

            const isLarge = amount >= 1000;

            return (
              <div key={tx.event_id || i}>
                {/* Desktop row */}
                <div
                  className="hide-mobile"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '80px minmax(0, 1.2fr) minmax(0, 1.2fr) 130px 90px 70px',
                    gap: '16px',
                    padding: '14px 24px',
                    borderBottom: '1px solid var(--border-subtle)',
                    alignItems: 'center',
                    background: isLarge ? 'var(--bg-subtle)' : 'transparent',
                    transition: 'background 0.15s ease',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-subtle)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = isLarge ? 'var(--bg-subtle)' : 'transparent'}
                >
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}>
                    <span style={{
                      color: 'var(--text-secondary)',
                      fontFamily: 'var(--font-mono)',
                      fontSize: '11px',
                      fontWeight: '500',
                    }}>
                      {kindLabel}
                    </span>
                  </div>

                  <div style={{ minWidth: 0 }}>
                    {senderName && (
                      <div style={{
                        color: 'var(--text-primary)',
                        fontSize: '13px',
                        fontWeight: '500',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}>
                        {senderName}
                      </div>
                    )}
                    <div style={{
                      color: senderName ? 'var(--text-tertiary)' : 'var(--text-secondary)',
                      fontFamily: 'var(--font-mono)',
                      fontSize: '10px',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}>
                      {truncateParty(sender)}
                    </div>
                  </div>

                  <div style={{ minWidth: 0 }}>
                    {receiver ? (
                      <>
                        {receiverName && (
                          <div style={{
                            color: 'var(--text-primary)',
                            fontSize: '13px',
                            fontWeight: '500',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}>
                            {receiverName}
                          </div>
                        )}
                        <div style={{
                          color: receiverName ? 'var(--text-tertiary)' : 'var(--text-secondary)',
                          fontFamily: 'var(--font-mono)',
                          fontSize: '10px',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}>
                          {truncateParty(receiver)}
                        </div>
                      </>
                    ) : (
                      <span style={{ color: 'var(--text-disabled)', fontSize: '12px' }}>—</span>
                    )}
                  </div>

                  <div style={{ textAlign: 'right' }}>
                    <div style={{
                      color: isLarge ? 'var(--text-primary)' : 'var(--text-secondary)',
                      fontFamily: 'var(--font-mono)',
                      fontWeight: isLarge ? '600' : '400',
                      fontSize: '13px',
                    }}>
                      {formatNumber(amount)} CC
                    </div>
                    <div style={{
                      color: 'var(--text-tertiary)',
                      fontFamily: 'var(--font-mono)',
                      fontSize: '10px',
                    }}>
                      ${formatNumber(usdAmount)}
                    </div>
                  </div>

                  <div style={{
                    textAlign: 'right',
                    color: 'var(--text-tertiary)',
                    fontFamily: 'var(--font-mono)',
                    fontSize: '11px',
                  }}>
                    #{tx.round?.toLocaleString()}
                  </div>

                  <div style={{
                    color: 'var(--text-tertiary)',
                    fontSize: '11px',
                    textAlign: 'right',
                  }}>
                    {formatTime(tx.date)}
                  </div>
                </div>

                {/* Mobile row */}
                <div
                  className="show-mobile-only"
                  style={{
                    display: 'flex',
                    padding: '12px',
                    borderBottom: '1px solid var(--border-subtle)',
                    alignItems: 'flex-start',
                    background: isLarge ? 'var(--bg-subtle)' : 'transparent',
                  }}
                >
                  <div style={{
                    width: '45px',
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '2px',
                  }}>
                    <span style={{
                      color: 'var(--text-secondary)',
                      fontFamily: 'var(--font-mono)',
                      fontSize: '9px',
                      fontWeight: '500',
                    }}>
                      {kindLabel.slice(0, 4)}
                    </span>
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      color: 'var(--text-primary)',
                      fontSize: '11px',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}>
                      {senderName || truncateParty(sender, 12)}
                    </div>
                    {receiver && (
                      <div style={{
                        color: 'var(--text-tertiary)',
                        fontSize: '10px',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}>
                        → {receiverName || truncateParty(receiver, 12)}
                      </div>
                    )}
                  </div>

                  <div style={{
                    width: '70px',
                    flexShrink: 0,
                    textAlign: 'right',
                  }}>
                    <div style={{
                      color: isLarge ? 'var(--text-primary)' : 'var(--text-secondary)',
                      fontFamily: 'var(--font-mono)',
                      fontWeight: isLarge ? '600' : '400',
                      fontSize: '10px',
                    }}>
                      {formatNumber(amount)}
                    </div>
                    <div style={{
                      color: 'var(--text-tertiary)',
                      fontFamily: 'var(--font-mono)',
                      fontSize: '9px',
                    }}>
                      ${formatNumber(usdAmount)}
                    </div>
                  </div>

                  <div style={{
                    width: '35px',
                    flexShrink: 0,
                    color: 'var(--text-tertiary)',
                    fontSize: '9px',
                    textAlign: 'right',
                  }}>
                    {formatTime(tx.date).replace(' ago', '')}
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-tertiary)' }}>
            {filterKind !== 'all' || filterMinAmount || filterMaxAmount
              ? 'No transactions match your filters'
              : 'No transactions found'
            }
          </div>
        )}
      </div>

      {/* Pagination */}
      <div style={{
        padding: '12px 16px',
        borderTop: '1px solid var(--border-default)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        background: 'var(--bg-subtle)',
      }}>
        <div style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>
          Showing {startIndex + 1}-{Math.min(startIndex + PAGE_SIZE, filteredTransactions.length)} of {filteredTransactions.length}
        </div>

        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button
            onClick={() => setCurrentPage(p => p - 1)}
            disabled={currentPage === 1 || isLoading}
            style={{
              background: 'var(--bg-elevated)',
              border: '1px solid var(--border-subtle)',
              color: currentPage === 1 ? 'var(--text-disabled)' : 'var(--text-primary)',
              padding: '6px 12px',
              borderRadius: '4px',
              fontSize: '12px',
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
            }}
          >
            Prev
          </button>
          <span style={{ fontSize: '12px', color: 'var(--text-secondary)', minWidth: '60px', textAlign: 'center' }}>
            {currentPage} / {totalPages || 1}
          </span>
          <button
            onClick={() => setCurrentPage(p => p + 1)}
            disabled={currentPage >= totalPages || isLoading}
            style={{
              background: 'var(--bg-elevated)',
              border: '1px solid var(--border-subtle)',
              color: currentPage >= totalPages ? 'var(--text-disabled)' : 'var(--text-primary)',
              padding: '6px 12px',
              borderRadius: '4px',
              fontSize: '12px',
              cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer',
            }}
          >
            Next
          </button>
        </div>
      </div>
    </section>
  );
}
